import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  consumePendingTask,
  markPendingTaskRan,
  wasPendingTaskRan,
} from "../lib/pendingTask.js";

/**
 * Run once when a page loads with a pending task from the home prompt box.
 */
export function usePendingTask(route, onRun) {
  const location = useLocation();
  const navigate = useNavigate();
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;

  useEffect(() => {
    const stored = consumePendingTask(route);
    const fromState =
      location.state?.pendingTask?.route === route
        ? location.state.pendingTask
        : null;
    const task = fromState || stored;

    if (!task?.prompt || task.autoRun === false) {
      if (fromState) {
        queueMicrotask(() =>
          navigate(location.pathname, { replace: true, state: {} })
        );
      }
      return;
    }

    if (wasPendingTaskRan(task)) {
      if (fromState) {
        queueMicrotask(() =>
          navigate(location.pathname, { replace: true, state: {} })
        );
      }
      return;
    }

    markPendingTaskRan(task);

    // Run immediately — a deferred run was being cancelled when router state cleared.
    onRunRef.current(task);

    if (fromState) {
      queueMicrotask(() =>
        navigate(location.pathname, { replace: true, state: {} })
      );
    }
    // Intentionally omit location.state — clearing it must not re-run this effect.
  }, [route, location.key, location.pathname, navigate]);
}

/** True after a home-started task finishes loading without error. */
export function useHomeTaskDone(loading, error, active) {
  const [done, setDone] = useState(false);
  const sawLoading = useRef(false);

  useEffect(() => {
    if (!active) {
      sawLoading.current = false;
      setDone(false);
      return;
    }

    if (loading) {
      sawLoading.current = true;
      setDone(false);
      return;
    }

    if (sawLoading.current && !error) {
      setDone(true);
    }
  }, [loading, error, active]);

  return done;
}
